/**
 * OCP-755 - key-only SSH for one server: the current state (last hardening
 * run, what sshd reports about password login), a read-only preflight
 * (effective sshd settings, blockers, password-only accounts, the keys that
 * can log in today) and the audited hardening run with a required reason and
 * an explicit blocker override.
 */
import { useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

export interface SshHardeningState {
  id: string;
  ssh_configured: boolean;
  ssh_auth_method?: 'key' | 'password' | 'none';
  ssh_hardened_at?: string | null;
  ssh_password_auth?: 'disabled' | 'enabled' | null;
  ssh_hardening?: HardeningResult | null;
}

interface Preflight {
  ok: boolean;
  failure: string | null;
  user: string | null;
  includeDir: boolean;
  matchBlocks: boolean;
  confPresent: boolean;
  effective: Record<string, string>;
  passwordOnlyAccounts: string[];
  authorizedKeys: string[];
}

interface HardeningResult {
  ok: boolean;
  stage: string;
  error: string | null;
  blockers: string[];
  overridden: boolean;
  switchedToKey: boolean;
  operatorKeysInstalled: number;
  effective: Record<string, string> | null;
  passwordRejected: boolean | null;
  at: string;
}

const STAGE_LABEL: Record<string, string> = {
  preflight: 'preflight',
  keys: 'installing keys',
  verify_key: 'proving the new key',
  sshd: 'sshd change',
  confirm: 'confirming on a new connection',
  password_check: 'checking the old password is refused',
  done: 'done',
};

export function SshHardeningPanel({ server, onChanged, flash }: { server: SshHardeningState; onChanged: () => Promise<void> | void; flash: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [pre, setPre] = useState<{ preflight: Preflight | null; blockers: string[]; error: string | null } | null>(null);
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState(false);
  const [result, setResult] = useState<HardeningResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const last = result ?? server.ssh_hardening ?? null;

  async function runPreflight() {
    if (checking) return;
    setChecking(true);
    setErr(null);
    try {
      setPre(await api(`/v1/admin/vps-servers/${server.id}/ssh/hardening/preflight`, { method: 'POST', body: {} }));
    } catch (e) {
      setErr(e instanceof ApiError ? `Preflight failed (HTTP ${e.status}): ${(e.body as { message?: string })?.message ?? ''}` : 'Preflight failed.');
    } finally {
      setChecking(false);
    }
  }

  async function harden() {
    if (running || reason.trim().length < 3) return;
    setRunning(true);
    setErr(null);
    try {
      const res = await api<{ ok: boolean; result: HardeningResult }>(`/v1/admin/vps-servers/${server.id}/ssh/harden`, {
        method: 'POST',
        body: { reason: reason.trim(), ...(override ? { override_blockers: true } : {}) },
      });
      setResult(res.result);
      if (res.ok) {
        flash('Server is key-only now');
        setReason('');
        setOverride(false);
        setOpen(false);
      }
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? `Hardening failed (HTTP ${e.status}).` : 'Hardening failed.');
    } finally {
      setRunning(false);
    }
  }

  const status = server.ssh_hardened_at && server.ssh_password_auth === 'disabled'
    ? { tone: 'text-green-700 dark:text-green-400', text: `Key-only since ${fmtDateTime(server.ssh_hardened_at)}` }
    : server.ssh_password_auth === 'enabled' || server.ssh_auth_method === 'password'
      ? { tone: 'text-amber-700 dark:text-amber-400', text: `Password login ${server.ssh_password_auth === 'enabled' ? 'allowed by sshd' : 'not checked'}${server.ssh_auth_method === 'password' ? ' - OCP still connects with a stored password' : ''}` }
      : { tone: 'text-gray-600 dark:text-gray-400', text: 'Not checked yet' };

  return (
    <div className="space-y-2" data-testid="ssh-hardening">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500">Key-only SSH:</span>
        <span className={status.tone} data-testid="ssh-hardening-status">{status.text}</span>
        <button
          type="button"
          disabled={!server.ssh_configured}
          onClick={() => {
            setOpen((o) => !o);
            if (!pre) void runPreflight();
          }}
          className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          data-testid="ssh-hardening-toggle"
        >
          {open ? 'Close' : 'Check / make key-only'}
        </button>
      </div>

      {open && (
        <div className="rounded border border-gray-200 dark:border-gray-800 p-3 space-y-3 text-xs">
          <p className="text-gray-600 dark:text-gray-400">
            OCP switches to its own key for this host (proven on a second connection before the stored password is deleted), installs every
            operator key from the setting <span className="font-mono">ocp.operator_ssh_public_keys</span>, turns password login off in sshd and
            checks the old password is refused. If the new sshd setting locks OCP out, the host undoes it by itself after 5 minutes.
          </p>

          {checking && <p className="text-gray-500">Reading the host…</p>}
          {pre?.preflight && (
            <div className="space-y-1.5" data-testid="ssh-hardening-preflight">
              <div>
                <span className="text-gray-500">sshd today:</span>{' '}
                <span className="font-mono">
                  PasswordAuthentication {pre.preflight.effective.passwordauthentication ?? '?'} · PermitRootLogin {pre.preflight.effective.permitrootlogin ?? '?'} · KbdInteractive{' '}
                  {pre.preflight.effective.kbdinteractiveauthentication ?? '?'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Keys that can log in as {pre.preflight.user ?? 'the SSH user'}:</span>
                {pre.preflight.authorizedKeys.length === 0 ? (
                  <span className="ml-1">none</span>
                ) : (
                  <ul className="ml-4 list-disc font-mono">
                    {pre.preflight.authorizedKeys.map((k) => (
                      <li key={k}>{k}</li>
                    ))}
                  </ul>
                )}
              </div>
              {pre.blockers.length > 0 ? (
                <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-2" data-testid="ssh-hardening-blockers">
                  <p className="font-medium text-amber-800 dark:text-amber-300">The run stops on:</p>
                  <ul className="ml-4 list-disc text-amber-800 dark:text-amber-300">
                    {pre.blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-green-700 dark:text-green-400">No blockers.</p>
              )}
            </div>
          )}
          {pre?.error && !pre.preflight && <p className="text-red-700 dark:text-red-400">{pre.error}</p>}

          <label className="block text-gray-600 dark:text-gray-400">
            Reason (audited)
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. SOC 2 / ISO 27001: key-only fleet access"
              className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1"
              data-testid="ssh-hardening-reason"
            />
          </label>
          {pre && pre.blockers.length > 0 && (
            <label className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="ssh-hardening-override" />
              Run anyway - the reason says why nobody is locked out
            </label>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={running || reason.trim().length < 3 || (!!pre && pre.blockers.length > 0 && !override)}
              onClick={() => void harden()}
              className="rounded bg-brand-600 px-3 py-1.5 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              data-testid="ssh-hardening-run"
            >
              {running ? 'Hardening… (up to a minute)' : 'Make key-only'}
            </button>
            <button type="button" disabled={checking} onClick={() => void runPreflight()} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 disabled:opacity-50">
              {checking ? 'Checking…' : 'Check again'}
            </button>
          </div>
        </div>
      )}

      {err && <p className="text-xs text-red-700 dark:text-red-400">{err}</p>}
      {last && (
        <div className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-2.5 text-xs" data-testid="ssh-hardening-last">
          {last.ok ? (
            <span className="text-green-700 dark:text-green-400">
              Last run {fmtDateTime(last.at)}: key-only · OCP {last.switchedToKey ? 'moved from its password to a new key' : 'kept its key'} · {last.operatorKeysInstalled} operator key(s)
              {last.passwordRejected === true ? ' · old password refused' : ''}
              {last.overridden ? ' · blockers overridden' : ''}
            </span>
          ) : (
            <span className="text-red-700 dark:text-red-400">
              Last run {fmtDateTime(last.at)} stopped at {STAGE_LABEL[last.stage] ?? last.stage}: {last.error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
